import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DropDownList from "../../../../components/Atomos/DropDown/Dropdown";
import NewButton from "../../../../components/Atomos/Buttons/newButton";
import Plus2 from "../../../../components/Atomos/Icons/Plus";
import Close from "../../../../components/Atomos/Icons/Close";
import FirmContactButton from "../../../../components/Atomos/Buttons/firmContactButton";
import SaveChanges from "../../../../components/Atomos/Icons/EditMode/SaveChanges";
import type { FirmContactView } from "../../../../interfaces/models/IFirmContact";
import type { IOfficeNew } from "../../../../interfaces/models/IOffice";
import { fetchPersonByFirm } from "../../../../Services/Person";
import { getOfficeByFirm } from "../../../../Services/Office";
import {
  getFirmContactByFirmId,
  saveFirmContact,
} from "../../../../Services/FirmContact";
import {
  contactsForFirmContactDropdown,
  formatPersonsForContactDropdown,
  resolveContactSelection,
  toFirmContactPayload,
  type ContactDropdownPerson,
} from "../../../../utils/firmContactUtils";
import { getJurisdictionLabel } from "../../../../utils/jurisdiction/structuredJurisdiction";

type props = {
  buttonClicked: string;
  update: any;
  setUpdate: any;
  firmContact: any;
  setErrorFields: any;
  state: any;
};

type ContactSelection = { id: string; name: string };

type SelectedContactsState = {
  primaryContact: ContactSelection;
  secondaryContact: ContactSelection;
  marketingContact: ContactSelection;
  accountingContact: ContactSelection;
  itContact: ContactSelection;
  socialMediaContact: ContactSelection;
  hrContact: ContactSelection;
  ccContact: ContactSelection;
};

type LegacyContactRow = {
  type?: string;
  contact?: string;
  name?: string;
};

const initialActiveErrors = {
  primaryContact: false,
  secondaryContact: false,
  marketingContact: false,
  accountingContact: false,
};

const ActiveErrorsMessage = {
  primaryContact: "Primary Contact is required.",
  secondaryContact: "Secondary Contact is required.",
  marketingContact: "Marketing Contact is required.",
  accountingContact: "Accounting Contact is required.",
};

const EMPTY_CONTACT: FirmContactView["primaryContact"] = {
  id: "",
  fullName: "",
  email: "",
  phone: { code: "", phone: "", basecode: "" },
};

const emptySelectedContacts = (): SelectedContactsState => ({
  primaryContact: { id: "", name: "" },
  secondaryContact: { id: "", name: "" },
  marketingContact: { id: "", name: "" },
  accountingContact: { id: "", name: "" },
  itContact: { id: "", name: "" },
  socialMediaContact: { id: "", name: "" },
  hrContact: { id: "", name: "" },
  ccContact: { id: "", name: "" },
});

function isMainOffice(office: Partial<IOfficeNew>): boolean {
  return String(office.officeType ?? "").toLowerCase() === "main";
}

function selectionFromContact(
  contact?: FirmContactView["primaryContact"],
): ContactSelection {
  return {
    id: String(contact?.id ?? ""),
    name: String(contact?.fullName ?? "").trim(),
  };
}

function contactFromSelection(
  selection: ContactSelection,
  persons: ContactDropdownPerson[],
): FirmContactView["primaryContact"] {
  if (!selection.id) {
    return { ...EMPTY_CONTACT };
  }
  const person = persons.find((row) => String(row.id ?? "") === selection.id);
  return {
    id: selection.id,
    fullName: selection.name,
    email: String(person?.firmEmail ?? ""),
    phone: { ...EMPTY_CONTACT.phone },
  };
}

function blockToUiState(block: FirmContactView): {
  selected: SelectedContactsState;
  liaison: ContactSelection[];
} {
  return {
    selected: {
      primaryContact: selectionFromContact(block.primaryContact),
      secondaryContact: selectionFromContact(block.secondaryContact),
      marketingContact: selectionFromContact(block.marketingContacts?.[0]),
      accountingContact: selectionFromContact(block.accountingContact),
      itContact: selectionFromContact(block.itContact),
      socialMediaContact: selectionFromContact(block.socialMediaContact),
      hrContact: selectionFromContact(block.hrContact),
      ccContact: selectionFromContact(block.ccContact),
    },
    liaison: (block.liaisonContacts ?? []).map((contact) =>
      selectionFromContact(contact),
    ),
  };
}

function buildBlockFromSelections(
  block: FirmContactView,
  selected: SelectedContactsState,
  liaisonSelections: ContactSelection[],
  persons: ContactDropdownPerson[],
): FirmContactView {
  return {
    ...block,
    primaryContact: contactFromSelection(selected.primaryContact, persons),
    secondaryContact: contactFromSelection(selected.secondaryContact, persons),
    accountingContact: contactFromSelection(
      selected.accountingContact,
      persons,
    ),
    marketingContacts: selected.marketingContact.id
      ? [contactFromSelection(selected.marketingContact, persons)]
      : [],
    itContact: selected.itContact.id
      ? contactFromSelection(selected.itContact, persons)
      : undefined,
    socialMediaContact: selected.socialMediaContact.id
      ? contactFromSelection(selected.socialMediaContact, persons)
      : undefined,
    hrContact: selected.hrContact.id
      ? contactFromSelection(selected.hrContact, persons)
      : undefined,
    ccContact: selected.ccContact.id
      ? contactFromSelection(selected.ccContact, persons)
      : undefined,
    liaisonContacts: liaisonSelections
      .filter((item) => item.id)
      .map((item) => contactFromSelection(item, persons)),
  };
}

function buildEmptyBlock(
  firmId: string,
  office: IOfficeNew,
  firmName: string,
): FirmContactView {
  return {
    id: "",
    firmId,
    officeId: String(office.id ?? "").trim(),
    firmName,
    jurisdiction: getJurisdictionLabel(office),
    createdDate: 0,
    primaryContact: { ...EMPTY_CONTACT },
    secondaryContact: { ...EMPTY_CONTACT },
    accountingContact: { ...EMPTY_CONTACT },
    marketingContacts: [],
  };
}

function normalizeLegacyBlock(
  source: Record<string, unknown>,
  firmId: string,
  office: IOfficeNew,
  firmName: string,
  persons: ContactDropdownPerson[],
): FirmContactView {
  const rows = (source.contacts as LegacyContactRow[] | undefined) ?? [];
  const pickSelection = (type: string): ContactSelection => {
    const entry = rows.find((row) => row.type === type);
    return resolveContactSelection(persons, entry?.contact);
  };

  const liaison = rows
    .filter((row) => row.type === "additionalContact")
    .map((row) => resolveContactSelection(persons, row.contact))
    .filter((item) => item.id);

  const marketing = pickSelection("marketingContact");

  return {
    id: String(source.id ?? ""),
    firmId,
    officeId: String(office.id ?? "").trim(),
    firmName,
    jurisdiction: getJurisdictionLabel(office),
    createdDate: Number(source.createdDate ?? 0),
    primaryContact: contactFromSelection(
      pickSelection("primaryContact"),
      persons,
    ),
    secondaryContact: contactFromSelection(
      pickSelection("secondaryContact"),
      persons,
    ),
    accountingContact: contactFromSelection(
      pickSelection("accountingContact"),
      persons,
    ),
    marketingContacts: marketing.id
      ? [contactFromSelection(marketing, persons)]
      : [],
    itContact: pickSelection("itContact").id
      ? contactFromSelection(pickSelection("itContact"), persons)
      : undefined,
    socialMediaContact: pickSelection("socialMediaContact").id
      ? contactFromSelection(pickSelection("socialMediaContact"), persons)
      : undefined,
    hrContact: pickSelection("hrContact").id
      ? contactFromSelection(pickSelection("hrContact"), persons)
      : undefined,
    ccContact: pickSelection("ccContact").id
      ? contactFromSelection(pickSelection("ccContact"), persons)
      : undefined,
    liaisonContacts: liaison.map((item) => contactFromSelection(item, persons)),
  };
}

function resolveFirmWideBlock(
  source: unknown,
  firmId: string,
  office: IOfficeNew,
  firmName: string,
  firmContacts: FirmContactView[],
  persons: ContactDropdownPerson[],
): FirmContactView {
  const fromApi = firmContacts.find(
    (row) =>
      String(row.officeId ?? "").trim() === String(office.id ?? "").trim(),
  );
  if (fromApi) {
    return fromApi;
  }

  const record = (source ?? {}) as Record<string, unknown>;
  if (record.primaryContact || record.primaryContactId) {
    return {
      ...(record as unknown as FirmContactView),
      firmId: String(record.firmId ?? record.firm ?? firmId).trim(),
      officeId: String(record.officeId ?? record.office ?? office.id).trim(),
      jurisdiction:
        String(record.jurisdiction ?? "").trim() ||
        getJurisdictionLabel(office),
    };
  }

  if (Array.isArray(record.contacts) && record.contacts.length > 0) {
    return normalizeLegacyBlock(record, firmId, office, firmName, persons);
  }

  return buildEmptyBlock(firmId, office, firmName);
}

const EditFirm: React.FC<props> = ({
  buttonClicked,
  firmContact,
  setErrorFields,
  state,
}) => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const saveButtonRef = useRef<HTMLDivElement>(null);
  const [selectedContacts, setSelectedContacts] = useState(
    emptySelectedContacts,
  );
  const [selectedLiaisonContacts, setSelectedLiaisonContacts] = useState<
    ContactSelection[]
  >([]);
  const [contacts, setContacts] = useState<ContactDropdownPerson[]>([]);
  const [contactBlock, setContactBlock] = useState<FirmContactView | null>(
    null,
  );
  const [persistedId, setPersistedId] = useState("");
  const [activeErrors, setActiveErrors] = useState(initialActiveErrors);
  const [activeErrorsMessage] = useState(ActiveErrorsMessage);

  const firmId = String(
    firmContact?.firmId ?? firmContact?.firm ?? state?.id ?? "",
  ).trim();
  const firmName = String(
    firmContact?.firmName ?? state?.firmName ?? state?.name ?? "",
  ).trim();

  const handleDropDownChange = (key: string, contact: ContactSelection) => {
    setSelectedContacts((prevState) => ({
      ...prevState,
      [key]: { id: contact.id, name: contact.name ?? "" },
    }));
  };

  useEffect(() => {
    const newErrorFields: string[] = [];
    if (activeErrors.primaryContact) {
      newErrorFields.push("Primary Contact");
    }
    if (activeErrors.secondaryContact) {
      newErrorFields.push("Secondary Contact");
    }
    if (activeErrors.marketingContact) {
      newErrorFields.push("Marketing Contact");
    }
    if (activeErrors.accountingContact) {
      newErrorFields.push("Accounting Contact");
    }
    setErrorFields(newErrorFields);
  }, [activeErrors, setErrorFields]);

  const handleDropDownChangeLiaison = (
    index: number,
    contact: ContactSelection,
  ) => {
    setSelectedLiaisonContacts((prevContacts) => {
      const updatedContacts = [...prevContacts];
      updatedContacts[index] = { id: contact.id, name: contact.name };
      return updatedContacts;
    });
  };

  const handleAddNewContact = () => {
    setSelectedLiaisonContacts((prevContacts) => [
      ...prevContacts,
      { id: "", name: "" },
    ]);
  };

  useEffect(() => {
    if (!firmId) {
      return;
    }

    const fromNavigation = formatPersonsForContactDropdown(state?.personList);
    if (fromNavigation.length > 0) {
      setContacts(fromNavigation);
      return;
    }

    let cancelled = false;

    const loadContacts = async () => {
      try {
        const response = await fetchPersonByFirm(firmId);
        if (cancelled) {
          return;
        }
        const data = (response.data ?? []).filter(
          (contact: { firm?: string }) => String(contact.firm ?? "") === firmId,
        );
        setContacts(formatPersonsForContactDropdown(data));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    loadContacts();

    return () => {
      cancelled = true;
    };
  }, [firmId, state?.personList]);

  useEffect(() => {
    if (!firmId) {
      return;
    }

    let cancelled = false;

    const loadBlock = async () => {
      try {
        const [offices, firmContacts] = await Promise.all([
          getOfficeByFirm(firmId),
          getFirmContactByFirmId(firmId),
        ]);
        if (cancelled || !offices.length) {
          return;
        }

        const mainOffice =
          offices.find(isMainOffice) ?? (offices[0] as IOfficeNew);
        const dropdownPersons =
          contacts.length > 0
            ? contacts
            : formatPersonsForContactDropdown(state?.personList);

        const block = resolveFirmWideBlock(
          firmContact,
          firmId,
          mainOffice,
          firmName,
          firmContacts,
          dropdownPersons,
        );

        setContactBlock(block);
        setPersistedId(String(block.id ?? "").trim());
      } catch (error) {
        console.error("Error loading firm-wide contact:", error);
      }
    };

    loadBlock();

    return () => {
      cancelled = true;
    };
  }, [firmId, firmContact, firmName, state?.personList]);

  useEffect(() => {
    if (!contactBlock) {
      return;
    }
    const ui = blockToUiState(contactBlock);
    setSelectedContacts(ui.selected);
    setSelectedLiaisonContacts(ui.liaison);
  }, [contactBlock]);

  const dropdowns = [
    {
      key: "primaryContact",
      title: "Primary Contact",
      selectedObjst: selectedContacts.primaryContact,
      required: true,
      error: activeErrors.primaryContact,
      errorMsj: activeErrorsMessage.primaryContact,
    },
    {
      key: "secondaryContact",
      title: "Secondary Contact",
      selectedObjst: selectedContacts.secondaryContact,
      required: true,
      error: activeErrors.secondaryContact,
      errorMsj: activeErrorsMessage.secondaryContact,
    },
    {
      key: "marketingContact",
      title: "Marketing Contact",
      selectedObjst: selectedContacts.marketingContact,
      required: true,
      error: activeErrors.marketingContact,
      errorMsj: activeErrorsMessage.marketingContact,
    },
    {
      key: "accountingContact",
      title: "Accounting Contact",
      selectedObjst: selectedContacts.accountingContact,
      required: true,
      error: activeErrors.accountingContact,
      errorMsj: activeErrorsMessage.accountingContact,
    },
    {
      key: "itContact",
      title: "IT Contact",
      selectedObjst: selectedContacts.itContact,
    },
    {
      key: "socialMediaContact",
      title: "Social Media Contact",
      selectedObjst: selectedContacts.socialMediaContact,
    },
    {
      key: "hrContact",
      title: "HR Contact",
      selectedObjst: selectedContacts.hrContact,
    },
    {
      key: "ccContact",
      title: "CC - Contact",
      selectedObjst: selectedContacts.ccContact,
    },
  ];

  const validateForm = () => {
    const newActiveErrors = {
      primaryContact: !selectedContacts.primaryContact.id,
      secondaryContact: !selectedContacts.secondaryContact.id,
      marketingContact: !selectedContacts.marketingContact.id,
      accountingContact: !selectedContacts.accountingContact.id,
    };
    setActiveErrors(newActiveErrors);
    return (
      newActiveErrors.primaryContact ||
      newActiveErrors.secondaryContact ||
      newActiveErrors.marketingContact ||
      newActiveErrors.accountingContact
    );
  };

  const handleUpdate = async () => {
    try {
      setChecked(true);

      if (buttonClicked !== "saveOverride") {
        if (validateForm()) {
          return;
        }
      }

      if (!contactBlock) {
        return;
      }

      const updatedBlock = buildBlockFromSelections(
        contactBlock,
        selectedContacts,
        selectedLiaisonContacts,
        contacts,
      );

      await saveFirmContact(
        toFirmContactPayload(updatedBlock, persistedId || undefined),
      );

      setTimeout(() => {
        navigate(`/ManageFirm/GeneralTab/${state.id}`, {
          state: { ...state, selectedTab: "GeneralTab" },
        });
      }, 500);
    } catch (error: unknown) {
      console.error("Error al actualizar el elemento:", error);
    }
  };

  useEffect(() => {
    if (checked) {
      validateForm();
    }
  }, [selectedContacts, checked]);

  return (
    <>
      <div className="flex flex-col gap-10 w-full">
        <div className="grid md:grid-cols-6 lg:grid-cols-9 gap-5">
          {dropdowns.map((dropdown, index) => (
            <div key={index} className="grid col-span-3 gap-1 w-full">
              <DropDownList
                title={dropdown.title}
                selectedObjst={dropdown.selectedObjst?.name ?? ""}
                getid={true}
                seter={(id: string) => {
                  const selected = resolveContactSelection(contacts, id);
                  handleDropDownChange(dropdown.key, selected);
                }}
                data={contactsForFirmContactDropdown(
                  contacts,
                  dropdown.key,
                  dropdown.selectedObjst?.id,
                )}
                error={dropdown.error ?? false}
                errormsj={dropdown.errorMsj ?? ""}
                required={dropdown.required ?? false}
              />
            </div>
          ))}
          {selectedLiaisonContacts.map((dropdown, index) => (
            <div key={index} className="grid col-span-3 gap-1 w-full ">
              <DropDownList
                title={"Additional Contact " + (index + 1)}
                getid={true}
                selectedObjst={selectedLiaisonContacts[index].name}
                seter={(id: string) => {
                  const selected = resolveContactSelection(contacts, id);
                  handleDropDownChangeLiaison(index, selected);
                }}
                data={contactsForFirmContactDropdown(
                  contacts,
                  "liaisonContact",
                  selectedLiaisonContacts[index]?.id,
                )}
                error={false}
              />
            </div>
          ))}
        </div>
        <div className="w-full md:w-min">
          <NewButton
            text={`NEW CONTACT`}
            color="accent"
            type="button"
            style="filled"
            size="medium"
            content="textIcon"
            onClick={handleAddNewContact}
            icon={<Plus2 />}
          />
        </div>
        <div className="flex justify-end self-stretch w-full">
          <div className="flex w-full md:w-min gap-4">
            <NewButton
              text={`CANCEL`}
              color="neutral"
              content="textIcon"
              type="button"
              style="outlined"
              size="medium"
              className=""
              icon={<Close />}
              onClick={() => {
                navigate(-1);
              }}
            />
            <div className="w-full min-w-[8.75rem]">
              <FirmContactButton
                text={`save`}
                color="accent"
                style="filled"
                size="medium"
                content="textIcon"
                icon={<SaveChanges />}
                onClick={handleUpdate}
                ref={saveButtonRef}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditFirm;
